import get from 'lodash/get';
import noop from 'lodash/noop';
import throttle from 'lodash/throttle';
import { eventChannel } from 'redux-saga';
import { call, cancel, cancelled, delay, fork, put, race, select, take } from 'redux-saga/effects';

import { openModal } from '../../../../actions/Modal';
import StillListeningBody from '../../../../components/StillListeningBody/StillListeningBody';
import { ApiActions } from '../../../../constants/ApiActions';
import * as ListenerUtils from '../../../../lib/ListenerUtils';
import * as TimeUtils from '../../../../lib/TimeUtils';
import Logger from '../../../../tools/Logger';
import * as IdleMonitorActions from '../../actions/IdleMonitor';
import * as IdleMonitorConstants from '../../constants/IdleMonitor';
import * as D from '../../deps';
import * as IdleMonitorUtils from '../../utils/IdleMonitorUtils';

const log = Logger.child({
  component: 'domains/Application/sagas/workers/IdleMonitor',
});

const LOOP = true;

/**
 * Adapter for transmitting DOM events to monitored channel. Used to tell whether or
 * not a listener has been 'active' in a given period of time.
 *
 * @param {Array} events - events to monitor
 * @returns {Channel<any>}
 */
// eslint-disable-next-line require-yield
function* createActivityChannel(events) {
  return eventChannel(emitter => {
    log.info('createActivityChannel(): starting event activity channel');
    events.forEach(event => {
      const handler = throttle(
        () => {
          log.trace(`createActivityChannel(): detecting event activity - ${event}`);
          emitter(event);
        },
        5000,
        { trailing: false },
      );
      window.addEventListener(event, handler);
    });
    // We never unsubscribe
    return noop;
  });
}

/**
 * Sub process worker spawned during a listeners initial transition to ABANDONED state
 * from INACTIVE. This worker waits for the the current track to change before prompting
 * the AYSL modal. Externally this process is canceled if the user becomes ACTIVE again
 * before track change.
 */
function* handleAYSLInteraction() {
  try {
    log.info('handleActiveToAbandoned(): worker started');
    yield take('AUDIO_PLAYING');
    log.info('handleActiveToAbandoned(): track change detected triggering AYSL');
    yield put(D.UseCases.pauseTrack({ logPlaybackActionsAnalytics: false }));
    yield put(
      openModal({
        modalType: 'ModalCustom',
        body: StillListeningBody,
      }),
    );
  } catch (e) {
    // nothing to handle but logging for traceability
    log.error('onBecomeAbandoned(): worker error', e);
  } finally {
    // no compensating transaction but logging for traceability
    if (yield cancelled()) {
      log.warn('onBecomeAbandoned(): worker cancelled');
    }
  }
}

/**
 * Monitors the current listener activity and determines when to transition to/from
 * INACTIVE idle status.
 *
 * Transition States
 * - ACTIVE -> INACTIVE
 * - INACTIVE -> ACTIVE
 *
 */
function* inactiveMonitor() {
  const { ACTIVE, INACTIVE } = IdleMonitorConstants.IdleMonitorStatuses;
  const activityChannel = yield call(createActivityChannel, ['keypress', 'mousedown', 'mousemove']);

  while (LOOP) {
    // wait for the following
    // 1) DOM activity on activityChannel
    // 2) expiration of inactiveThreshold timer
    const inactiveThresholdMS = TimeUtils.secondsToMillis(
      yield select(IdleMonitorUtils.getIdleThreshold, INACTIVE),
    );
    const { timeout } = yield race({
      activity: take(activityChannel),
      timeout: delay(inactiveThresholdMS),
    });

    // handle status change
    const currentStatus = yield select(IdleMonitorUtils.getIdleCurrentStatus);
    log.trace('inactiveMonitor(): processing current status', currentStatus);
    if (timeout) {
      // ACTIVE -> INACTIVE
      if (currentStatus === ACTIVE) {
        log.info('inactiveMonitor(): setting idle status to INACTIVE');
        yield put(IdleMonitorActions.updateIdleMonitor(INACTIVE));
      }
    } else {
      // everything else is considered activity
      // INACTIVE -> ACTIVE
      if (currentStatus === INACTIVE) {
        log.info('inactiveMonitor(): setting idle status to ACTIVE');
        yield put(IdleMonitorActions.updateIdleMonitor(ACTIVE));
      }
    }
  }
}

/**
 * Monitors the current listener activity and determines when to transition to/from
 * ABANDONED idle status.
 *
 * Transition States
 * - ACTIVE/INACTIVE -> ABANDONED
 * - ABANDONED -> ACTIVE
 *
 * @note: Upon entering ABANDONED idle status we fork a AYSL process that monitors for the
 * next opportunity to display AYSL modal. Currently, we listen for the AUDIO_PLAYING
 * event which signifies the earliest point we know of a song change while still being
 * able to pause the tuner.
 */
function* abandonedMonitor() {
  const { ACTIVE, ABANDONED } = IdleMonitorConstants.IdleMonitorStatuses;
  const activityChannel = yield call(createActivityChannel, ['keypress', 'mousedown']);
  // todo: WEBCLIENT-3926: move idleMonitor to Listener Domain
  // eslint-disable-next-line import/no-deprecated
  const isPremiumListener = yield select(ListenerUtils.listenerIsPremium);
  let activeWorker = null;

  while (LOOP) {
    // wait for the following
    // 1) DOM activity on activityChannel
    // 2) expiration of abandonedThreshold timer
    const abandonedThresholdMS = TimeUtils.secondsToMillis(
      yield select(IdleMonitorUtils.getIdleThreshold, ABANDONED),
    );
    log.trace(`abandonedMonitor(): setting abandonedThresholdMS ${abandonedThresholdMS}`);
    const { timeout } = yield race({
      activity: take(activityChannel),
      timeout: delay(abandonedThresholdMS),
    });

    // handle status change
    const currentStatus = yield select(IdleMonitorUtils.getIdleCurrentStatus);
    log.trace('abandonedMonitor(): processing current status', currentStatus);
    if (timeout) {
      // ACTIVE/INACTIVE -> ABANDONED
      if (currentStatus !== ABANDONED && !isPremiumListener) {
        log.info('abandonedMonitor(): setting idle status to ABANDONED');
        yield put(IdleMonitorActions.updateIdleMonitor(ABANDONED));
        activeWorker = yield fork(handleAYSLInteraction);
      }
    } else {
      // everything else is considered activity
      // ABANDONED -> ACTIVE
      if (currentStatus === ABANDONED) {
        // were previously not active so set to active
        log.info('idleMonitor(): setting idle status to ACTIVE');
        yield put(IdleMonitorActions.updateIdleMonitor(ACTIVE));
        if (activeWorker) {
          yield cancel(activeWorker);
        }
      }
    }
  }
}

/**
 * Worker for monitoring the current listeners idle states. The listener has three states that they can be
 * in at any given time (ACTIVE, INACTIVE, ABANDONED). The responsibility for transitioning between the states
 * is shared between two sub processes that handle transition states affecting inactive and abandoned.
 *
 * INACTIVE - Unique to the web client. This state signifies that if a user is ad supported and not on now playing we
 * will transition the user to NowPlaying to maximize displayAd exposure.
 *
 * ABANDONED - Referred to as Inactive everywhere but the web client. In the ABANDONED state we wait for the next
 * track change to prompt a AYSL (Are you still listening?) modal. If the user becomes active before the track change
 * we cancel the AYSL worker and set the user back to ACTIVE.
 */
export function* idleMonitorWorker() {
  yield put(IdleMonitorActions.startIdleMonitor());
  yield fork(inactiveMonitor);
  yield fork(abandonedMonitor);
}

/**
 * Watches for not reauth registered user sign-ons in order to start a new
 * idle monitoring session
 */
export function* idleMonitor() {
  let worker = null;
  while (LOOP) {
    const action = yield take([ApiActions.LOGIN_SUCCESS, ApiActions.REGISTER_SUCCESS]);
    if (!get(action, 'params.actionParams.isReauth')) {
      if (worker) yield cancel(worker);
      worker = yield call(idleMonitorWorker);
    }
  }
}
